import { cva, type VariantProps } from "class-variance-authority";
import { Eye, EyeOff } from "lucide-react";
import * as React from "react";

import { Button } from "./button";

import { cn } from "@/common/lib/utils";

const inputVariants = cva(
  "text-sm font-normal leading-5 flex w-full rounded-md border border-input bg-background ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      size: {
        sm: "h-6 px-2 py-1 text-xs",
        md: "h-10 px-3 py-2",
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
);

export interface InputProps
  extends Omit<React.ComponentProps<"input">, "size">,
    VariantProps<typeof inputVariants> {
  /** Текст, отображаемый слева от input, всегда видимый */
  prefix?: string;
  /** Текст, отображаемый справа от input, всегда видимый */
  postfix?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    { className, type, prefix, postfix, size = "md", onChange, value, onBlur, onFocus, ...props },
    ref,
  ) => {
    const [showPassword, setShowPassword] = React.useState(false);
    const isPassword = type === "password";
    const isDateTime = type === "datetime-local" || type === "date" || type === "time";
    const isNumber = type === "number";

    const isIOS =
      typeof navigator !== "undefined" && /iP(ad|hone|od)/.test(navigator.userAgent || "");
    const isIOSDateTime = isIOS && isDateTime;
    const shouldUseDefaultValueHack = isDateTime && !isIOS;

    const inputType = isPassword && showPassword ? "text" : type;

    // Вычисляем левый отступ в зависимости от наличия prefix
    const leftPadding = prefix ? "pl-20" : "";

    // Вычисляем правый отступ в зависимости от наличия postfix и кнопки пароля
    const rightPadding = postfix
      ? "pr-24" // место только для postfix
      : isPassword
        ? "pr-10" // место только для кнопки пароля
        : "";

    const isSmall = size === "sm";

    // Обработчик для перехвата ввода до попадания в DOM (onBeforeInput)
    const handleBeforeInput = React.useCallback(
      (e: React.FormEvent<HTMLInputElement>) => {
        if (!isNumber) return;

        const inputEvent = e.nativeEvent as InputEvent;
        const inputData = inputEvent.data;

        // Если вводимый символ не является цифрой, точкой или минусом - предотвращаем ввод
        if (inputData && !/^[0-9.-]$/.test(inputData)) {
          e.preventDefault();
          return;
        }
      },
      [isNumber],
    );

    // Обработчик для Safari: очистка defaultValue при фокусе (ограничен для избежания багов iOS)
    const handleFocus = React.useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        if (shouldUseDefaultValueHack && e.currentTarget.defaultValue) {
          e.currentTarget.defaultValue = "";
        }
        if (onFocus) {
          onFocus(e);
        }
      },
      [shouldUseDefaultValueHack, onFocus],
    );

    return (
      <div className="relative">
        {prefix && (
          <span
            className={cn(
              "absolute top-0 left-0 flex h-full items-center text-muted-foreground pointer-events-none z-10",
              isSmall ? "text-xs px-2" : "text-sm px-3",
            )}
          >
            {prefix}
          </span>
        )}
        <input
          type={inputType}
          className={cn(
            inputVariants({ size }),
            leftPadding,
            rightPadding,
            isIOSDateTime && (isSmall ? "block py-0 leading-[1.5rem]" : "block py-0 leading-[2.5rem]"),
            className,
          )}
          ref={ref}
          {...(value !== undefined ? { value } : {})}
          onBeforeInput={isNumber ? handleBeforeInput : undefined}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={shouldUseDefaultValueHack ? handleFocus : onFocus}
          {...props}
        />
        {postfix && (
          <span
            className={cn(
              "absolute top-0 flex h-full items-center text-muted-foreground pointer-events-none",
              isSmall ? "text-xs" : "text-sm",
              isPassword ? "right-10 px-2" : "right-0 px-3",
            )}
          >
            {postfix}
          </span>
        )}
        {isPassword && (
          <Button
            tabIndex={-1}
            type="button"
            variant="ghost"
            size="icon"
            className={cn(
              "absolute right-0 top-0 h-full hover:bg-transparent",
              isSmall ? "px-1.5" : "px-3 py-2",
            )}
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? "Скрыть пароль" : "Показать пароль"}
          >
            {showPassword ? (
              <EyeOff className={cn("text-muted-foreground", isSmall ? "h-3 w-3" : "h-4 w-4")} />
            ) : (
              <Eye className={cn("text-muted-foreground", isSmall ? "h-3 w-3" : "h-4 w-4")} />
            )}
          </Button>
        )}
      </div>
    );
  },
);
Input.displayName = "Input";

export { Input, inputVariants };
